import React, { useState, useEffect } from "react";
import { get } from "../services/smartApiService";

interface PricePattern {
  id: number;
  company_id: number;
  symbol: string;
  company_name: string | null;
  timestamp: string;
  timeframe: string;
  pattern_type: string;
  confidence: number;
  volume_confirmed: boolean;
  signal: string;
  details: any;
  created_at: string;
}

interface PricePatternsTableProps {
  symbol?: string;
  companyId?: number;
  timeframe?: string;
  signal?: string;
  patternType?: string;
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // in seconds
  showCompanyColumn?: boolean;
  showTimeframeFilter?: boolean;
  title?: string;
}

const PricePatternsTable: React.FC<PricePatternsTableProps> = ({
  symbol,
  companyId,
  timeframe: initialTimeframe,
  signal,
  patternType,
  limit = 50,
  autoRefresh = false,
  refreshInterval = 60,
  showCompanyColumn = true,
  showTimeframeFilter = false,
  title = "Price Patterns",
}) => {
  const [patterns, setPatterns] = useState<PricePattern[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>(initialTimeframe || "");
  const [isMinimized, setIsMinimized] = useState<boolean>(false);

  const fetchPatterns = () => {
    setLoading(true);
    setError(null);

    // Build query params
    const params = new URLSearchParams();
    if (symbol) params.append("symbol", symbol);
    if (companyId) params.append("company_id", companyId.toString());
    if (selectedTimeframe) params.append("timeframe", selectedTimeframe);
    if (signal) params.append("signal", signal);
    if (patternType) params.append("pattern_type", patternType);
    params.append("limit", limit.toString());

    get(`/api/options/price-patterns?${params.toString()}`, {
      showLoading: false,
    }).subscribe({
      next: (response: any) => {
        if (response.data.success) {
          setPatterns(response.data.patterns || []);
          setTotal(response.total || 0);
        } else {
          setError("Failed to load patterns");
        }
        setLoading(false);
      },
      error: (err: any) => {
        console.error("Failed to fetch price patterns:", err);
        setError(err?.message || "Failed to load patterns");
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchPatterns();

    // Auto refresh if enabled
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchPatterns();
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [symbol, companyId, selectedTimeframe, signal, patternType, limit]);

  const getSignalColor = (signal: string): string => {
    switch (signal.toLowerCase()) {
      case "bullish":
        return "has-text-success";
      case "bearish":
        return "has-text-danger";
      default:
        return "has-text-grey";
    }
  };

  const getSignalIcon = (signal: string): string => {
    switch (signal.toLowerCase()) {
      case "bullish":
        return "fa-arrow-up";
      case "bearish":
        return "fa-arrow-down";
      default:
        return "fa-minus";
    }
  };

  const formatPatternType = (type: string): string => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="box">
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <h3 className="title is-5">
              {title}
              {total > 0 && (
                <span className="tag is-info is-light ml-2">{total}</span>
              )}
            </h3>
          </div>
        </div>
        <div className="level-right">
          {showTimeframeFilter && (
            <div className="level-item">
              <div className="select is-small">
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                >
                  <option value="">All Timeframes</option>
                  <option value="15min">15 Min</option>
                  <option value="30min">30 Min</option>
                  <option value="1hour">1 Hour</option>
                  <option value="4hour">4 Hour</option>
                  <option value="1day">1 Day</option>
                  <option value="1week">1 Week</option>
                </select>
              </div>
            </div>
          )}
          <div className="level-item">
            <button
              className={`button is-small is-info ${loading ? "is-loading" : ""}`}
              onClick={fetchPatterns}
              disabled={loading}
            >
              <span className="icon is-small">
                <i className="fa fa-refresh"></i>
              </span>
              <span>Refresh</span>
            </button>
          </div>
          <div className="level-item">
            <button
              className="button is-small"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Maximize" : "Minimize"}
            >
              <span className="icon is-small">
                <i className={`fa fa-${isMinimized ? "window-maximize" : "window-minimize"}`}></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Minimized View */}
      {isMinimized ? (
        <div className="notification is-light py-2 px-3">
          <div className="is-flex is-justify-content-space-between is-align-items-center">
            <div>
              <strong className="is-size-7">Recent Patterns: </strong>
              {patterns.slice(0, 3).map((p, idx) => (
                <span key={p.id}>
                  <span className={`tag is-small ml-1 ${
                    p.signal === 'bullish' ? 'is-success' :
                    p.signal === 'bearish' ? 'is-danger' : 'is-warning'
                  }`}>
                    {formatPatternType(p.pattern_type)} ({p.timeframe})
                  </span>
                  {idx < 2 && idx < patterns.slice(0, 3).length - 1 && ", "}
                </span>
              ))}
              {patterns.length > 3 && (
                <span className="tag is-small is-light ml-1">+{patterns.length - 3} more</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className="notification is-danger is-light">
              <button className="delete" onClick={() => setError(null)}></button>
              {error}
            </div>
          )}

          {loading && patterns.length === 0 ? (
            <div className="has-text-centered py-5">
              <button className="button is-loading is-large is-white"></button>
            </div>
          ) : patterns.length === 0 ? (
            <div className="notification is-info is-light">
              <p className="has-text-centered">No patterns found</p>
            </div>
          ) : (
        <div className="table-container">
          <table className="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                {showCompanyColumn && (
                  <>
                    <th>Symbol</th>
                    <th>Company</th>
                  </>
                )}
                <th>Pattern</th>
                <th>Signal</th>
                <th>Timeframe</th>
                <th>Confidence</th>
                <th>Volume</th>
                <th>Detected At</th>
              </tr>
            </thead>
            <tbody>
              {patterns.map((pattern) => (
                <tr key={pattern.id}>
                  {showCompanyColumn && (
                    <>
                      <td>
                        <strong>{pattern.symbol}</strong>
                      </td>
                      <td className="is-size-7">{pattern.company_name || "-"}</td>
                    </>
                  )}
                  <td>
                    <span className="tag is-light">
                      {formatPatternType(pattern.pattern_type)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`icon-text ${getSignalColor(pattern.signal)}`}
                    >
                      <span className="icon">
                        <i className={`fa ${getSignalIcon(pattern.signal)}`}></i>
                      </span>
                      <span className="has-text-weight-semibold">
                        {pattern.signal.toUpperCase()}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className="tag is-info is-light">
                      {pattern.timeframe}
                    </span>
                  </td>
                  <td>
                    <div className="is-flex is-align-items-center">
                      <progress
                        className={`progress is-small ${
                          pattern.confidence >= 0.7
                            ? "is-success"
                            : pattern.confidence >= 0.5
                            ? "is-warning"
                            : "is-danger"
                        }`}
                        value={pattern.confidence * 100}
                        max="100"
                        style={{ width: "60px" }}
                      >
                        {(pattern.confidence * 100).toFixed(0)}%
                      </progress>
                      <span className="ml-2 is-size-7">
                        {(pattern.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td>
                    {pattern.volume_confirmed ? (
                      <span className="icon has-text-success" title="Volume confirmed">
                        <i className="fa fa-check-circle"></i>
                      </span>
                    ) : (
                      <span className="icon has-text-grey-light" title="No volume confirmation">
                        <i className="fa fa-circle"></i>
                      </span>
                    )}
                  </td>
                  <td className="is-size-7">
                    {formatTimestamp(pattern.timestamp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}

          {patterns.length > 0 && total > limit && (
            <div className="notification is-info is-light">
              <p className="is-size-7 has-text-centered">
                Showing {patterns.length} of {total} patterns
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PricePatternsTable;
